/**
 * Swedish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const sv: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Översikt',
  'nav.funds': 'Fonder',
  'nav.investments': 'Investeringar',
  'nav.investees': 'Investeringsmottagare',
  'nav.esg': 'ESG Granskning',
  'nav.compliance': 'Regelefterlevnad',
  'nav.reports': 'Rapporter',
  'nav.settings': 'Inställningar',
  'nav.users': 'Användare',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indikatorer',
  'nav.taxonomy': 'EU-taxonomi',
  'nav.filings': 'Inlämningar',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Skicka data',
  'nav.submissions': 'Mina inlämningar',
  'nav.benchmarks': 'Referensvärden',
  'nav.messages': 'Meddelanden',
  'nav.portfolio': 'Portfölj',
  'nav.performance': 'Fondresultat',
  'nav.esgSummary': 'ESG Sammanfattning',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Effektmått',
  'nav.sdg': 'SDG-kartläggning',
  'nav.audit': 'Revisionsspår',
  'nav.snapshots': 'Dataögonblicksbilder',
  'nav.knowledgeGraph': 'Kunskapsgraf',
  'nav.geographic': 'Geografisk',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Uppgifter',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorisk monitor',

  // Common Actions
  'action.create': 'Skapa',
  'action.edit': 'Redigera',
  'action.delete': 'Radera',
  'action.save': 'Spara',
  'action.cancel': 'Avbryt',
  'action.submit': 'Skicka',
  'action.approve': 'Godkänn',
  'action.reject': 'Avvisa',
  'action.download': 'Ladda ner',
  'action.export': 'Exportera',
  'action.import': 'Importera',
  'action.filter': 'Filtrera',
  'action.search': 'Sök',
  'action.refresh': 'Uppdatera',
  'action.close': 'Stäng',
  'action.back': 'Tillbaka',
  'action.next': 'Nästa',
  'action.previous': 'Föregående',
  'action.viewAll': 'Visa alla',
  'action.viewDetails': 'Visa detaljer',
  'action.generate': 'Generera',
  'action.signOut': 'Logga ut',
  'action.addNew': 'Lägg till ny',
  'action.invite': 'Bjud in',

  // Statuses
  'status.draft': 'Utkast',
  'status.submitted': 'Inskickat',
  'status.validated': 'Validerat',
  'status.approved': 'Godkänt',
  'status.rejected': 'Avvisat',
  'status.pending': 'Väntande',
  'status.inProgress': 'Pågående',
  'status.overdue': 'Försenat',
  'status.compliant': 'Uppfyllt',
  'status.filed': 'Inlämnat',
  'status.active': 'Aktiv',
  'status.inactive': 'Inaktiv',
  'status.closed': 'Stängd',
  'status.fundraising': 'Kapitalanskaffning',
  'status.investing': 'Investering',
  'status.harvesting': 'Skörd',
  'status.validating': 'Validering',
  'status.review': 'Under granskning',
  'status.monitoring': 'Övervakning',
  'status.exiting': 'Utträde',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Utbetalad',
  'status.restructured': 'Omstrukturerad',
  'status.matured': 'Förfallen',
  'status.written_off': 'Avskriven',
  'status.exited': 'Avslutad',
  'status.incomplete': 'Ofullständig',
  'status.open': 'Öppen',
  'status.in_progress': 'Pågående',
  'status.completed': 'Slutförd',
  'status.cancelled': 'Avbruten',
  'status.accepted': 'Accepterad',
  'status.failed': 'Misslyckad',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portföljöversikt',
  'dashboard.complianceStatus': 'Efterlevnadsstatus',
  'dashboard.collectionProgress': 'Insamlingsframsteg',
  'dashboard.activityFeed': 'Aktivitet',
  'dashboard.quickActions': 'Snabbåtgärder',
  'dashboard.investmentPipeline': 'Investeringspipeline',
  'dashboard.totalFunds': 'Totalt fonder',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktiva investeringar',
  'dashboard.totalInvestees': 'Investeringsmottagare',
  'dashboard.recentActivity': 'Senaste aktivitet',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Totalt åtagande',
  'lp.capitalCalled': 'Inropat kapital',
  'lp.distributed': 'Distribuerat',
  'lp.netPosition': 'Nettoposition',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondresultat',
  'lp.esgSummary': 'ESG Sammanfattning',
  'lp.reportLibrary': 'Rapportbibliotek',
  'lp.taxonomyAligned': 'Taxonomianpassad',
  'lp.paiCoverage': 'PAI Täckning',
  'lp.portfolioPerformance': 'LP Portföljresultat',

  // Fund Labels
  'fund.name': 'Fondnamn',
  'fund.code': 'Fondkod',
  'fund.vintage': 'Grundläggningsår',
  'fund.size': 'Fondstorlek',
  'fund.targetSize': 'Målstorlek',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategi',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategori',
  'fund.geographyFocus': 'Geografiskt fokus',
  'fund.sectorFocus': 'Sektorfokus',

  // Form Labels
  'form.name': 'Namn',
  'form.email': 'E-post',
  'form.country': 'Land',
  'form.amount': 'Belopp',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Beskrivning',
  'form.notes': 'Anteckningar',
  'form.type': 'Typ',
  'form.period': 'Period',
  'form.year': 'År',
  'form.required': 'Obligatorisk',
  'form.optional': 'Valfritt',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Åtagande',
  'table.called': 'Inropat',
  'table.distributed': 'Distribuerat',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Reglering',
  'table.filingType': 'Inlämningstyp',
  'table.reportingPeriod': 'Period',
  'table.createdAt': 'Skapad',
  'table.actions': 'Åtgärder',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taxonomi',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datakompletthet',
  'compliance.nextDeadline': 'Nästa deadline',

  // Reports
  'report.quarterly': 'Kvartalsrapport',
  'report.annual': 'Årsrapport',
  'report.paiStatement': 'PAI Uttalande',
  'report.taxonomyReport': 'Taxonomirapport',
  'report.generatedAt': 'Genererad',
  'report.version': 'Version',

  // General
  'general.loading': 'Laddar...',
  'general.noData': 'Ingen data tillgänglig',
  'general.error': 'Ett fel uppstod',
  'general.retry': 'Försök igen',
  'general.total': 'Totalt',
  'general.page': 'Sida',
  'general.of': 'av',
  'general.showing': 'Visar',
  'general.results': 'resultat',
  'general.language': 'Språk',
  'general.english': 'Engelska',
  'general.french': 'Franska',

  // Tour
  'tour.takeTour': 'Starta rundtur',
  'tour.skip': 'Hoppa over',
  'tour.next': 'Nasta',
  'tour.prev': 'Foregaende',
  'tour.finish': 'Slutfor',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigation',
  'tour.sidebarContent': 'Använd sidofältet för att navigera mellan portföljhantering, ESG-datainsamling, regelefterlevnad, rapporter och analysverktyg.',
  'tour.kpisTitle': 'Portföljöversikt',
  'tour.kpisContent': 'Dessa kort visar din portfölj i en överblick — totala fonder, AUM, aktiva investeringar och antal investeringsmottagare.',
  'tour.actionsTitle': 'Snabbåtgärder',
  'tour.actionsContent': 'Skapa fonder, lägg till investeringar, bjud in mottagare och generera rapporter med ett klick.',
  'tour.languageTitle': 'Språk',
  'tour.languageContent': 'Växla mellan 24 officiella EU-språk. Hela gränssnittet översätts omedelbart.',
  'tour.aiTitle': 'AI-assistent',
  'tour.aiContent': 'Ställ frågor om din portfölj när som helst. AI-assistenten har full kontext om dina fonder och investeringar.',
  'action.createFund': 'Skapa fond',
  'action.editFund': 'Redigera fond',
  'action.addInvestment': 'Lägg till investering',
  'action.addInvestee': 'Lägg till mottagare',
  'action.editInvestee': 'Redigera mottagare',
  'action.createTemplate': 'Skapa mall',
  'action.createSubmission': 'Skapa inlämning',
  'action.createFiling': 'Skapa inlämning',
  'action.generateReport': 'Generera rapport',
  'action.inviteUser': 'Bjud in användare',
  'action.sendInvitation': 'Skicka inbjudan',
  'action.saveChanges': 'Spara ändringar',
  'form.fundName': 'Fondnamn',
  'form.fundCode': 'Fondkod',
  'form.sfdrCategory': 'SFDR Kategori',
  'form.baseCurrency': 'Basvaluta',
  'form.targetSize': 'Målstorlek',
  'form.vintageYear': 'Grundläggningsår',
  'form.geographyFocus': 'Geografiskt fokus',
  'form.sectorFocus': 'Sektorfokus',
  'form.investmentCode': 'Investeringskod',
  'form.instrumentType': 'Instrumenttyp',
  'form.maturityDate': 'Förfallodag',
  'form.sector': 'Sektor',
  'form.countryCode': 'Landskod',
  'form.entityType': 'Enhetstyp',
  'form.region': 'Region',
  'form.staffCount': 'Antal anställda',
  'form.borrowers': 'Låntagare',
  'form.contactName': 'Kontaktnamn',
  'form.contactEmail': 'Kontakt-e-post',
  'form.templateName': 'Mallnamn',
  'form.templateType': 'Malltyp',
  'form.indicators': 'Indikatorer',
  'form.reportingPeriod': 'Rapporteringsperiod',
  'form.reportingYear': 'Rapporteringsår',
  'form.regulation': 'Reglering',
  'form.filingType': 'Inlämningstyp',
  'form.reportType': 'Rapporttyp',
  'form.emailAddress': 'E-postadress',
  'form.role': 'Roll',
  'form.selectFund': 'Välj fond...',
  'form.selectInvestee': 'Välj mottagare...',
  'table.code': 'Kod',
  'table.investee': 'Mottagare',
  'table.instrument': 'Instrument',
  'table.amount': 'Belopp',
  'table.currency': 'Valuta',
  'table.maturity': 'Förfallodag',
  'table.name': 'Namn',
  'table.country': 'Land',
  'table.type': 'Typ',
  'table.period': 'Period',
  'table.completion': 'Slutförande',
  'table.submitted': 'Inskickat',
  'table.version': 'Version',
  'table.indicators': 'Indikatorer',
  'table.dueDate': 'Förfallodag',
  'table.generated': 'Genererat',
  'table.email': 'E-post',
  'table.role': 'Roll',
  'error.createFailed': 'Skapandet misslyckades. Försök igen.',
  'error.updateFailed': 'Uppdateringen misslyckades. Försök igen.',
  'error.loadFailed': 'Inläsning av data misslyckades.',
};
